/**
 * Runtime configuration.
 *
 * Read from /config.json at boot, NOT from import.meta.env. That is deliberate:
 * a Vite env var is baked into the bundle at build time, which would mean one
 * image per environment and a rebuild to change an endpoint. AnkhOS ships ONE
 * image; the ConfigMap writes /config.json at pod start, so a flag flip or an
 * endpoint change is a rollout, not a rebuild.
 */
export interface OidcConfig {
  authority: string;
  clientId: string;
  /** ZITADEL project id. Also the audience the backends require. */
  projectId: string;
}

/**
 * MSG91's OTP widget, used by the sign-in form to send and check the SMS code.
 *
 * 🔑 PUBLIC BY DESIGN: the widget id and its tokenAuth are handed to every
 * browser that loads the widget. tokenAuth is the WIDGET token, never the
 * MSG91 account authkey -- that one lives only in radaccess (Secret
 * radaccess-msg91), which confirms each widget result server-side.
 */
export interface Msg91WidgetConfig {
  widgetId: string;
  tokenAuth: string;
}

export interface AppConfig {
  gatewayBase: string;
  oidc: OidcConfig;
  flags: Record<string, boolean>;
  /** Base URL of the PHP portal, for deep links to screens not yet migrated. */
  phpPortalBase: string;
  /** Absent = no widget: the form falls back to the ZITADEL-sent SMS code. */
  msg91?: Msg91WidgetConfig;
  /**
   * Cloudflare Turnstile site key (PUBLIC), the human check on every way in
   * (auth/human.ts). Empty = no widget.
   */
  turnstileSiteKey?: string;
  /**
   * Test mobile prefixes (owner 2026-10-24), TEST console only: a 10-digit
   * number starting with one is accepted as a mobile (domain/testMobile.ts).
   */
  testMobilePrefixes?: string[];
}

let loaded: AppConfig | null = null;

/**
 * ⛔ There is NO default client id, and this throws rather than falling back.
 *
 * The obvious substitute — radaccess's own ZITADEL client — is a CONFIDENTIAL
 * backend client: it accepts a code exchange with no secret and sends no PKCE.
 * A browser client and a backend client must never share a registration, so a
 * missing client id has to be loud. A silent fallback here would produce a
 * working login that is quietly the wrong security model.
 */
export async function loadConfig(): Promise<AppConfig> {
  if (loaded) return loaded;

  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`config.json: HTTP ${res.status}`);
  const raw = (await res.json()) as Partial<AppConfig>;

  const missing: string[] = [];
  if (!raw.gatewayBase) missing.push("gatewayBase");
  if (!raw.oidc?.authority) missing.push("oidc.authority");
  if (!raw.oidc?.clientId) missing.push("oidc.clientId");
  if (!raw.oidc?.projectId) missing.push("oidc.projectId");
  if (missing.length > 0) {
    throw new Error(`config.json is missing required keys: ${missing.join(", ")}`);
  }

  // Both halves or neither: the entrypoint writes empty strings when the
  // widget is not configured for this environment.
  const widgetId = raw.msg91?.widgetId?.trim() ?? "";
  const tokenAuth = raw.msg91?.tokenAuth?.trim() ?? "";

  loaded = {
    gatewayBase: raw.gatewayBase!.replace(/\/+$/, ""),
    oidc: raw.oidc as OidcConfig,
    flags: raw.flags ?? {},
    // Runtime, not build-time: the parallel run ends by changing a ConfigMap,
    // not by rebuilding an image.
    phpPortalBase: (raw.phpPortalBase ?? "https://hotspot.ankhnet.in").replace(/\/+$/, ""),
    ...(widgetId && tokenAuth ? { msg91: { widgetId, tokenAuth } } : {}),
    turnstileSiteKey: (raw.turnstileSiteKey ?? "").trim(),
    testMobilePrefixes: String(raw.testMobilePrefixes ?? "").split(",").map((p) => p.trim()).filter(Boolean),
  };
  return loaded;
}

export function config(): AppConfig {
  if (!loaded) throw new Error("loadConfig() has not completed");
  return loaded;
}

export function flagEnabled(name: string): boolean {
  return config().flags[name] === true;
}
